import invariant from "invariant";
import MappingVisitor from "data/util/MappingVisitor";
import {
    ERR_MULTIPLE_MATCHES_FOUND,
    ERR_NO_MATCH_FOUND,
    isExcluded,
} from "data/MappingStore";
import entityMetaStore from "data/meta/entityMetaStore";
import EntityProxy from "data/util/EntityProxy";
import { FT_CHILD } from "data/meta/MappingInfoStore";

class TableMappingVisitor extends MappingVisitor {
    constructor(state, access, snapshot) {
        super(state, access, snapshot);
        this._indexStack = [];
        /*
         * This is a pretty kludgey way of doing this, but it _is_ correct. It'd
         * be better to actually enumerate the downstreams for each changed
         * field, and then only do that set of fields on the next pass.
         */
        const ek = this.state.target.entityKey;
        const ei = entityMetaStore.getInfo(ek);
        invariant(
            ei.maxDepGraphSet >= 0,
            "%s's 'maxDepGraphSet' is negative?!",
            ek,
        );
        this.endDepSetIndex = ei.maxDepGraphSet + 1; // half open
    }

    execute(ctx) {
        invariant(
            this._indexStack.length === 0,
            "Someone's bad at corner cases...",
        );
        for (let idx = 0; idx < this.endDepSetIndex; idx += 1) {
            this._fieldSetIndex = idx;
            super.execute(ctx);
        }
        invariant(
            this._indexStack.length === 0,
            "Someone's bad at corner cases...",
        );
        return this;
    }

    _catchUp(
        ctx,
        target,
        sourceOrSources,
        edgeInfo,
        endIndex = this._fieldSetIndex,
    ) {
        this._indexStack.push(this._fieldSetIndex);
        try {
            this.__forEach(sourceOrSources, source => {
                for (let idx = 0; idx < endIndex; idx += 1) {
                    this._fieldSetIndex = idx;
                    this.doRecord(ctx, target, false, source, edgeInfo);
                }
            });
        } finally {
            this._fieldSetIndex = this._indexStack.pop();
        }
    }

    _isDepSet(fieldInfo) {
        return fieldInfo.depGraphSet === this._fieldSetIndex;
    }

    shouldDoField(ctx, target, excluded, fieldInfo) {
        if (!super.shouldDoField(ctx, target, excluded, fieldInfo)) return false;
        return this._isDepSet(fieldInfo);
    }

    shouldDoCrossTree(ctx, target, excluded, fieldInfo) {
        if (!super.shouldDoCrossTree(ctx, target, excluded, fieldInfo)) return false;
        return this._isDepSet(fieldInfo);
    }

    doField(ctx, target, excluded, fieldInfo, sourceValue) {
        const value = excluded
            ? this.__oldValue(target, fieldInfo)
            : sourceValue;
        this._set(target, fieldInfo, value);
    }

    _set(target, fieldInfo, value) {
        this.access.updateEntityRecordField(
            target.entityKey,
            target.id,
            fieldInfo.key,
            value,
        );
    }

    doMagicRoot(ctx, target, excluded, fieldInfo, sourceValue) {
        invariant(!excluded, "Magic roots can't be excluded");
        invariant(
            sourceValue instanceof EntityProxy,
            "Magic root isn't single-valued?!",
        );
        // assert already present and wired.
        const lo = this.__getTargetIdLO(sourceValue, target, fieldInfo);
        invariant(
            lo.hasValue(),
            "Magic root isn't wired in mapping?!",
        );
        const id = lo.getValueEnforcing();
        invariant(
            this.access.hasEntityRecord(fieldInfo.otherEntityKey, id),
            "Magic root doesn't exist?!",
        );
        const curr = target.tableProxy.get(fieldInfo.logicalColumn);
        invariant(
            curr === id,
            "Mapping is attempting to change a magic root (from %s to %s w/in %s.%s)?!",
            curr,
            id,
            target.tableProxy.tableKey,
            fieldInfo.name
        );
        // Nothing to actually do, since we've vetted there's nothing to do.
        super.doMagicRoot(ctx, target, excluded, fieldInfo, sourceValue);
    }

    /*
    When an assoc is first wired, we need to immediately process all prior
    depSetIndex on _just_ that subtree, before passing it to super (which
    will do the current depSetIndex).
     */

    doGlobalAssoc(
        ctx,
        target,
        excluded,
        fieldInfo,
        sourceValue,
        createIfMissing,
    ) {
        const newValue = [];
        this.__forEach(
            excluded
                ? this.__oldValue(target, fieldInfo)
                : sourceValue,
            it => {
                const lo = this.__getTargetIdLO(it, target, fieldInfo);

                // Can't do anything if we're waiting for an answer
                if (!lo.isDone() || lo.isEmpty()) return;

                invariant(
                    lo.hasValue() || lo.hasError(),
                    "Global LO is empty (for %s.%s)?!",
                    target.entityKey,
                    fieldInfo.key,
                );
                if (lo.hasError()) {
                    const err = lo.getErrorEnforcing();
                    if (err === ERR_NO_MATCH_FOUND) {
                        // skip it
                    } else if (err === ERR_MULTIPLE_MATCHES_FOUND) {
                        // skip it
                    } else if (lo.hasValue()) {
                        // leave whatever was there...
                        newValue.push(lo.getValueEnforcing());
                    }
                    return;
                }

                // Wire it into the tree
                const id = lo.getValueEnforcing();
                if (!isExcluded(this.state, target.id, fieldInfo.logicalColumn, id)) {
                    newValue.push(id);
                    if (!this.access.hasEntityRecord(
                        fieldInfo.otherEntityKey,
                        id,
                    )) {
                        this.access.createEntityRecord(
                            fieldInfo.otherEntityKey,
                            id,
                        );
                        // Catch it up
                        this._catchUp(ctx, target, it, fieldInfo);
                    }
                }
            },
        );


        if (!excluded) {
            if (this._isDepSet(fieldInfo)) {
                this._set(
                    target,
                    fieldInfo,
                    this.__cleanMultiplicity(fieldInfo, newValue),
                );
            }
            super.doGlobalAssoc(
                ctx,
                target,
                excluded,
                fieldInfo,
                sourceValue,
                createIfMissing
            );
        }
    }

    doComposition(ctx, target, excluded, fieldInfo, sourceValue) {
        this.__forEach(sourceValue, it => {
            const lo = this.__getTargetIdLO(it, target, fieldInfo);

            // No value means it hasn't been through MappingStore yet, which
            // means it's the child of a just-wired record. Ignore for now, and
            // it'll get processed on the next iteration (which is guaranteed to
            // occur, because a new record was wired).
            if (!lo.hasValue()) return;

            // Wire it into the tree
            const id = lo.getValueEnforcing();

            const childExists = this.access.hasEntityRecord(
                fieldInfo.otherEntityKey,
                id,
            );
            if (isExcluded(this.state, target.id, fieldInfo.logicalColumn, id)) {
                if (childExists) {
                    this.access.deleteEntityChild(
                        target.entityInfo,
                        target.id,
                        fieldInfo,
                        id,
                    );
                }
            } else {
                if (!childExists) {
                    // create it!
                    this.access.createEntityChild(
                        target.entityInfo,
                        target.id,
                        fieldInfo,
                        id,
                    );
                    // Catch it up
                    this._catchUp(ctx, target, it, fieldInfo);
                }
            }
        });

        // Keep on walking
        super.doComposition(ctx, target, excluded, fieldInfo, sourceValue);
    }

    doAutomanaged(
        ctx,
        target,
        excluded,
        fieldInfo,
        autoManagedByFieldInfo,
        sourceValue,
    ) {
        this.__forEach(sourceValue, s => {
            const t = this.__getAutomanagedTarget(target, s, fieldInfo);
            if (t == null) return;
            this.doRecord(ctx, t, false, s, FT_CHILD, fieldInfo);
        });
    }

    doCrossTree(ctx, target, excluded, fieldInfo, sourceValue) {
        invariant(!excluded, "Cross-tree associations can't be excluded");
        // For each owner pair, get the source CTA's ids, translate them to
        // the the corresponding target CTA's ids and set the ids in.
        const oek = fieldInfo.otherEntityKey;
        const ids = [];
        this.__forEach(sourceValue, it => {
            const lo = this.__getTargetIdLO(it, target, fieldInfo);
            if (!lo.hasValue()) return;
            const id = lo.getValueEnforcing();
            if (!this.access.hasEntityRecord(oek, id)) return;
            ids.push(id);
        });
        this._set(target, fieldInfo, ids);
    }

}

export default TableMappingVisitor;



// WEBPACK FOOTER //
// ./app/data/TableMappingVisitor.js